import { useState } from "react";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { AlertTriangle } from "lucide-react";
import { commissionsApi, type CommissionClaimDetailDto } from "@/api/commissions";
import { ApiError } from "@/api/client";
import { commissionKeys } from "@/hooks/commissions/commissionKeys";
import { Button, FormField, Input } from "@/design-system/components/ui";
import { Modal } from "@/components/ui/Modal";
import { currency, percent } from "@/components/commissions/formatters";

interface ResolveEscalationModalProps {
  open: boolean;
  onClose: () => void;
  claim: CommissionClaimDetailDto;
}

/**
 * The arbiter's door. `resolveEscalation` has existed on the server and on this
 * API client since the module shipped and had ZERO callers — an escalated claim
 * rendered a badge and incremented a tile, and nothing in the product could act
 * on it. This is that missing control.
 *
 * It is the most consequential transition in the module and the copy says so:
 * it forces the claim to Agreed with no status guard of its own, and a pinned
 * commission amount is written through VERBATIM rather than recomputed from
 * gross x percent. Leaving all three number fields blank is the ordinary case —
 * "the claimant's figures stand" — and recomputes the amount.
 */
export function ResolveEscalationModal({ open, onClose, claim }: ResolveEscalationModalProps) {
  const qc = useQueryClient();
  const [resolution, setResolution] = useState("");
  const [setGross, setSetGross] = useState("");
  const [setPct, setSetPct] = useState("");
  const [setAmount, setSetAmount] = useState("");
  const [error, setError] = useState<string | null>(null);
  const c = claim.summary;

  const mutation = useMutation({
    mutationFn: () =>
      commissionsApi.resolveEscalation(c.id, {
        resolution: resolution.trim(),
        setGrossRevenue: setGross ? Number(setGross) : null,
        setCommissionPercent: setPct ? Number(setPct) : null,
        setCommissionAmount: setAmount ? Number(setAmount) : null,
      }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: commissionKeys.all });
      onClose();
    },
    onError: (e) => setError(e instanceof ApiError ? (e.errors[0] ?? e.message) : String(e)),
  });

  const canSubmit = resolution.trim().length > 0 && !mutation.isPending;

  return (
    <Modal
      open={open}
      onClose={onClose}
      dismissOnBackdropClick={false}
      title={`Resolve escalation on ${c.claimNumber}`}
      description="Your decision settles this claim: it moves to Agreed and the dispute closes. Leave the figures blank to let the claimant's stand."
      size="lg"
      footer={
        <>
          <Button variant="secondary" onClick={onClose} disabled={mutation.isPending}>
            Cancel
          </Button>
          <Button
            variant="primary"
            disabled={!canSubmit}
            loading={mutation.isPending}
            onClick={() => {
              setError(null);
              mutation.mutate();
            }}
          >
            Record decision
          </Button>
        </>
      }
    >
      {error && (
        <div role="alert" className="flex items-start gap-space-2 p-space-3 border border-status-error rounded-md bg-status-error/5 text-status-error text-small mb-space-3">
          <AlertTriangle size={16} strokeWidth={1.5} aria-hidden />
          <span>{error}</span>
        </div>
      )}

      <div className="flex flex-col gap-space-4">
        <div>
          <div className="font-display text-label font-semibold uppercase tracking-label text-gold-shadow mb-space-2">
            As filed (round {c.reviewRound})
          </div>
          <dl className="grid grid-cols-3 gap-space-3 m-0">
            <div>
              <dt className="text-caption text-gray-mid m-0">Gross revenue</dt>
              <dd className="text-small tabular-nums text-onyx m-0">{currency(c.grossRevenue)}</dd>
            </div>
            <div>
              <dt className="text-caption text-gray-mid m-0">Commission %</dt>
              <dd className="text-small tabular-nums text-onyx m-0">
                {percent(c.commissionPercent)}
              </dd>
            </div>
            <div>
              <dt className="text-caption text-gray-mid m-0">Commission</dt>
              <dd className="text-small tabular-nums text-onyx m-0">
                {currency(c.commissionAmount)}
              </dd>
            </div>
          </dl>
        </div>

        <FormField
          label="Resolution"
          required
          helperText="Both sides see this. Say what you decided and why."
        >
          <textarea
            value={resolution}
            onChange={(e) => setResolution(e.target.value)}
            rows={4}
            className="w-full px-space-3 py-space-2 border border-border-light rounded-md font-body text-small bg-white-pure focus:border-gold focus:outline-none"
          />
        </FormField>

        <div>
          <div className="font-display text-label font-semibold uppercase tracking-label text-gold-shadow mb-space-2">
            Pin values (optional)
          </div>
          <div className="grid grid-cols-3 gap-space-3">
            <FormField label="Gross revenue">
              <Input
                type="number"
                step="0.01"
                min={0}
                value={setGross}
                onChange={(e) => setSetGross(e.target.value)}
                placeholder="unchanged"
              />
            </FormField>
            <FormField label="Commission %">
              <Input
                type="number"
                step="0.01"
                min={0}
                max={100}
                value={setPct}
                onChange={(e) => setSetPct(e.target.value)}
                placeholder="unchanged"
              />
            </FormField>
            <FormField label="Commission amount">
              <Input
                type="number"
                step="0.01"
                min={0}
                value={setAmount}
                onChange={(e) => setSetAmount(e.target.value)}
                placeholder="recomputed"
              />
            </FormField>
          </div>
          <p className="text-caption text-gray-mid mt-space-1">
            Leave all three blank and the claimant&apos;s figures stand. A commission amount you
            type here is written through exactly as entered — it is NOT recomputed from gross ×
            percent, so it overrides both.
          </p>
        </div>
      </div>
    </Modal>
  );
}
